// client/src/components/pages/RouteSummary/LoadMatchingPreview.tsx (ST-00218 P2, SPEC §5)
//
// "Preview new matching" in the Brought / Carried Forward block of Route Summary: users with
// load_matching.view can see, read-only, what the new load matching would give this route for
// this period — BF, CF, missing loads, receipts with no dispatch, adjusted dispatched and the
// variance — beside what the summary shows now. One route at a time (a combined summary has no
// single route to preview). Hidden when load matching is switched off on the server
// (GET /status mode 'off'), and once the route itself uses the new matching. Nothing on the
// summary changes.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  getCompare,
  getStatus,
  isSwitchedOff,
  loadMatchingErrorText,
} from '../../../services/loadMatching';
import type { CompareRouteRow, LoadMatchingMode } from '../../../types/loadMatching';
import { bridgeSentence } from '../RouteIntelligence/LoadMatching/HeadlineStrip';
import { RouteNotes } from '../RouteIntelligence/LoadMatching/RouteTable';
import { fmtN, fmtPct, fmtT, isNa } from '../RouteIntelligence/LoadMatching/format';

export interface LoadMatchingPreviewProps {
  /** The routes the summary on screen was generated with. */
  groupingIds: number[];
  startDate: string;
  endDate: string;
  /** The summary's Min weight; the preview always uses 5 t. */
  minWeight?: number | null;
}

/** The engine's minimum weight (SPEC §1.1). */
const ENGINE_MIN_WEIGHT = 5;

export default function LoadMatchingPreview({
  groupingIds,
  startDate,
  endDate,
  minWeight,
}: LoadMatchingPreviewProps) {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('load_matching.view') || hasPermission('load_matching.manage');
  const single = groupingIds.length === 1 ? (groupingIds[0] as number) : null;
  const [mode, setMode] = useState<LoadMatchingMode | null>(null);
  const [on, setOn] = useState(false);
  const [row, setRow] = useState<CompareRouteRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Is load matching on at all? (One cheap call; a failure hides the toggle.)
  useEffect(() => {
    if (!allowed || single === null) return;
    let cancelled = false;
    getStatus()
      .then((s) => {
        if (!cancelled) setMode(s.mode);
      })
      .catch(() => {
        if (!cancelled) setMode('off');
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, single]);

  // The preview figures, fetched when the toggle is on (again when the summary changes).
  useEffect(() => {
    if (!on || single === null) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    getCompare({ startDate, endDate, groupingIds: [single] }, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        setRow(res.routes.find((r) => r.groupingId === single) ?? null);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        if (isSwitchedOff(err)) {
          setMode('off');
          return;
        }
        setError(loadMatchingErrorText(err, 'The preview could not be loaded.'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [on, single, startDate, endDate]);

  if (!allowed || single === null || mode === null || mode === 'off') return null;
  // The route already uses the new matching (and the server applies it): nothing to preview.
  if (mode === 'on' && row?.loadMatching === 'engine') return null;

  const na = row ? isNa(row.new) : false;
  const naOr = (v: string) => (na ? 'n/a' : v);

  return (
    <div
      className="px-4 sm:px-6 py-3 border-t border-slate-200 bg-slate-50/60"
      data-testid="load-matching-preview"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => setOn((v) => !v)}
          className="inline-flex items-center gap-2 select-none"
        >
          <span
            aria-hidden="true"
            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
              on ? 'bg-primary-600' : 'bg-slate-300'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                on ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </span>
          <span className="text-xs font-semibold text-slate-800">Preview new matching</span>
        </button>
        <span className="text-[11px] text-slate-500">
          Read-only — Brought / Carried Forward with the new load matching. The summary above does
          not change.
        </span>
      </div>

      {on && (
        <div className="mt-3" aria-live="polite">
          {loading && !row && (
            <p className="text-xs text-slate-500">Matching this route's loads…</p>
          )}
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
          {!loading && !error && !row && (
            <p className="text-xs text-slate-500">This route has no loads in this period.</p>
          )}
          {row && (
            <div className={loading ? 'opacity-60' : ''}>
              <div className="overflow-x-auto">
                <table className="w-full max-w-2xl text-xs" aria-label="Current vs new matching">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left font-semibold py-1 pr-3" />
                      <th className="text-right font-semibold py-1 px-3">Now</th>
                      <th className="text-right font-semibold py-1 pl-3 text-primary-700">
                        New matching
                      </th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {[
                      [
                        'Brought forward',
                        `${fmtN(row.old.bfN)} loads`,
                        naOr(`${fmtN(row.new.bfN)} loads`),
                      ],
                      [
                        'Carried forward',
                        `${fmtN(row.old.cfN)} loads`,
                        naOr(`${fmtN(row.new.cfN)} loads`),
                      ],
                      ['Missing loads', '—', naOr(`${fmtN(row.new.missingN)} loads`)],
                      // engine 1.1.0: dispatched again before a receipt (stays in dispatched)
                      ...(typeof row.new.needsReviewN === 'number'
                        ? [['Needs review', '—', naOr(`${fmtN(row.new.needsReviewN)} loads`)]]
                        : []),
                      ['Receipts with no dispatch', '—', `${fmtN(row.new.orphanN)} loads`],
                      [
                        'Adjusted dispatched',
                        fmtT(row.old.adjustedT),
                        naOr(fmtT(row.new.adjustedT)),
                      ],
                      ['Variance', fmtPct(row.old.variancePct), fmtPct(row.new.variancePct)],
                    ].map(([label, now, next]) => (
                      <tr key={label} className="border-t border-slate-200">
                        <th
                          scope="row"
                          className="text-left font-medium text-slate-600 py-1.5 pr-3"
                        >
                          {label}
                        </th>
                        <td className="text-right text-slate-600 px-3 whitespace-nowrap">{now}</td>
                        <td className="text-right font-semibold text-slate-900 pl-3 whitespace-nowrap">
                          {next}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <RouteNotes row={row} />
              {!na && (
                <p className="mt-2 text-[11px] text-slate-600">{bridgeSentence(row.new.bridge)}</p>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                Both columns count loads of 5 t and more
                {minWeight !== undefined && minWeight !== null && minWeight !== ENGINE_MIN_WEIGHT
                  ? `; this summary uses ${minWeight} t, so its figures can differ`
                  : ''}
                . The changed loads, data quality and sign-off are in{' '}
                <Link
                  className="font-medium text-primary-700 hover:underline"
                  to={`/route-intelligence?tab=load-matching&groupingIds=${single}&startDate=${startDate}&endDate=${endDate}&route=${single}`}
                >
                  Route Intelligence → Load matching
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
